// src/utils/driverPoints.js
// Adjust this path if your files.config is somewhere else.
import { ACC_FILES, AC_FILES } from "../results/files.config";

// Standard GT-style points
const POINTS_TABLE = [25, 18, 15, 12, 10, 8, 6, 4, 2, 1];
export const DROP_ROUNDS = 5;

/**
 * Normalize driver IDs:
 * - ACC uses "S7656..." (playerId)
 * - AC uses "7656..."  (DriverGuid)
 */
function normalizeId(id) {
  if (!id) return "";
  const s = String(id);
  return s.startsWith("S") ? s.slice(1) : s;
}

function getPointsForPosition(positionIndex) {
  return POINTS_TABLE[positionIndex] ?? 0;
}

/**
 * ------------- fetchJson (copied from Results.jsx pattern) -------------
 */

function stripBom(s) {
  return s && s.charCodeAt(0) === 0xfeff ? s.slice(1) : s;
}
function isGzip(u8) {
  return u8 && u8.length > 2 && u8[0] === 0x1f && u8[1] === 0x8b; // 1F 8B
}
async function gunzipIfSupported(buf) {
  if (typeof DecompressionStream === "function") {
    const ds = new DecompressionStream("gzip");
    const stream = new Response(buf).body.pipeThrough(ds);
    return await new Response(stream).arrayBuffer();
  }
  return buf;
}
function tryParseWithDecoders(buf) {
  const decoders = ["utf-8", "utf-16le", "utf-16be"];
  for (const enc of decoders) {
    try {
      const txt = new TextDecoder(enc, { fatal: false }).decode(buf);
      return JSON.parse(stripBom(txt));
    } catch {
      // try next
    }
  }
  throw new Error("Unrecognized encoding");
}

/**
 * ------------- Local cache (prevents fetching same file twice) -------------
 * Cache the Promise so concurrent callers share the same request/parse.
 * You can clear this between screens/tests if needed.
 */
const __jsonPromiseCache = new Map(); // url -> Promise<json>
export function clearDriverPointsFetchCache() {
  __jsonPromiseCache.clear();
}

async function fetchJsonUncached(url) {
  try {
    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    let buf = await res.arrayBuffer();
    let u8 = new Uint8Array(buf);

    if (isGzip(u8)) {
      try {
        buf = await gunzipIfSupported(buf);
        u8 = new Uint8Array(buf);
      } catch (e) {
        console.error("Gzip decode failed", e);
        return { __error: `Gzip decode failed: ${e.message}` };
      }
    }

    try {
      return tryParseWithDecoders(buf);
    } catch (e) {
      console.error("Encoding parse failed", e);
      return { __error: `Unrecognized encoding: ${e.message}` };
    }
  } catch (e) {
    console.error("fetchJson error", e);
    return { __error: e.message || String(e) };
  }
}

async function fetchJson(url) {
  const key = String(url);

  if (__jsonPromiseCache.has(key)) {
    return __jsonPromiseCache.get(key);
  }

  const p = (async () => {
    const json = await fetchJsonUncached(key);
    return json;
  })();

  __jsonPromiseCache.set(key, p);

  // If it fails in a catastrophic way (should be rare since fetchJsonUncached catches),
  // remove it so later calls can retry.
  p.catch(() => {
    __jsonPromiseCache.delete(key);
  });

  return p;
}

/**
 * ------------- Minimal parsers for points (learned from Results.jsx) -------------
 */

/**
 * Parse ACC session into a leaderboard suitable for points.
 * Only used when json.sessionType === "R".
 */
function parseACCRace(json) {
  if (!json || json.__error) return [];

  const sr = json.sessionResult || {};
  const lines = Array.isArray(sr.leaderBoardLines) ? sr.leaderBoardLines : [];

  return lines.map((line, index) => {
    const car = line.car || {};
    const drv = (car.drivers && car.drivers[0]) || line.currentDriver || {};

    const playerId = drv.playerId || "";
    return {
      positionIndex: index, // 0-based
      driverId: normalizeId(playerId),
    };
  });
}

/**
 * Parse AC session into a leaderboard suitable for points.
 * Only used when json.Type === "RACE".
 * Mirrors the lap-count & time sort from parseAC in Results.jsx.
 */
function parseACRace(json) {
  if (!json || json.__error) return [];

  const results = Array.isArray(json.Result) ? json.Result : [];
  const laps = Array.isArray(json.Laps) ? json.Laps : [];

  // Count laps per driver
  const lapCounts = {};
  laps.forEach((lap) => {
    const guid = lap.DriverGuid;
    if (guid) {
      lapCounts[guid] = (lapCounts[guid] || 0) + 1;
    }
  });

  // Build raw leaderboard and sort same as Results.jsx
  const leaderboard = results
    .map((r) => ({
      guid: r.DriverGuid || "",
      totalTimeMs: r.TotalTime,
      lapCount: lapCounts[r.DriverGuid] || 0,
    }))
    .sort((a, b) => {
      // Sort by laps completed (descending), then by total time (ascending)
      if (b.lapCount !== a.lapCount) return b.lapCount - a.lapCount;
      // handle missing times gracefully
      const at = Number.isFinite(a.totalTimeMs) ? a.totalTimeMs : Number.MAX_SAFE_INTEGER;
      const bt = Number.isFinite(b.totalTimeMs) ? b.totalTimeMs : Number.MAX_SAFE_INTEGER;
      return at - bt;
    });

  return leaderboard.map((entry, index) => ({
    positionIndex: index, // 0-based
    driverId: normalizeId(entry.guid),
  }));
}

/**
 * Helper: return an array of all race positions (0-based) for a driver.
 * e.g. [0, 2, 5] => P1 win, P3 podium, P6 non-podium
 */
async function getDriverRacePositions(driverId, sim = "ACC", currentSeasonOnly = false) {
  const targetId = normalizeId(driverId);
  if (!targetId) return [];

  const files = sim === "AC" ? AC_FILES : ACC_FILES;
  const positions = [];

  for (const f of files) {
    // Build URL exactly like Results.jsx
    const url = process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/${f}` : `/${f}`;
    const json = await fetchJson(url);

    const current_season = f.split("/").pop().startsWith("26");
    if (json.__error) continue;
    if (currentSeasonOnly && !current_season) continue;

    if (sim === "ACC") {
      // Only consider race sessions
      if (json.sessionType !== "R") continue;
      const leaderboard = parseACCRace(json);
      const idx = leaderboard.findIndex((e) => e.driverId === targetId);
      if (idx === -1) continue;
      positions.push(idx);
    } else {
      if (json.Type !== "RACE") continue;
      const leaderboard = parseACRace(json);
      const idx = leaderboard.findIndex((e) => e.driverId === targetId);
      if (idx === -1) continue;
      positions.push(idx);
    }
  }
  return positions;
}

/**
 * --------------------------------------------------------------------
 * Main API: getDriverPoints(driverId, "ACC" | "AC")
 * --------------------------------------------------------------------
 *
 * @param {string} driverId  e.g. "76561198033554024" or "S76561198033554024"
 * @param {string} sim       "ACC" or "AC"
 * @returns {Promise<number>} total points across all race sessions
 */
export async function getDriverPoints(driverId, sim = "ACC") {
  return (await getDriverDropBreakdown(driverId, sim)).points;
}

/**
 * Count the number of race wins (P1 finishes).
 */
export async function getDriverWins(driverId, sim = "ACC") {
  const positions = await getDriverRacePositions(driverId, sim);
  return positions.filter((pos) => pos === 0).length;
}

/**
 * Count the number of podiums (P1–P3 finishes).
 */
export async function getDriverPodiums(driverId, sim = "ACC") {
  const positions = await getDriverRacePositions(driverId, sim);
  return positions.filter((pos) => pos >= 0 && pos <= 2).length;
}

export async function getDriverDropBreakdown(driverId, sim, currentSeasonOnly = false) {
  sim = sim || 'ACC';
  const targetId = normalizeId(driverId);
  const files = sim === 'AC' ? AC_FILES : ACC_FILES;
  const rounds = [];
  for (const f of files) {
    if (currentSeasonOnly && !String(f).split('/').pop().startsWith('26')) continue;
    const url = process.env.PUBLIC_URL ? process.env.PUBLIC_URL + '/' + f : '/' + f;
    const json = await fetchJson(url);
    if (json.__error) continue;
    const isRace = sim === 'ACC' ? json.sessionType === 'R' : json.Type === 'RACE';
    if (!isRace) continue;
    const leaderboard = sim === 'ACC' ? parseACCRace(json) : parseACRace(json);
    const positionIndex = leaderboard.findIndex((entry) => entry.driverId === targetId);
    rounds.push({ fileName: f, points: positionIndex < 0 ? 0 : getPointsForPosition(positionIndex) });
  }
  const dropped = [...rounds].sort((a, b) => a.points - b.points || a.fileName.localeCompare(b.fileName)).slice(0, DROP_ROUNDS);
  const grossPoints = rounds.reduce((sum, round) => sum + round.points, 0);
  const droppedPoints = dropped.reduce((sum, round) => sum + round.points, 0);
  return { points: grossPoints - droppedPoints, grossPoints, droppedPoints, droppedFiles: dropped.map((round) => round.fileName) };
}

export async function getCurrentDriverPoints(driverId, sim = "ACC") {
  return (await getDriverDropBreakdown(driverId, sim, true)).points;
}

/**
 * Count the number of race wins (P1 finishes).
 */
export async function getCurrentDriverWins(driverId, sim = "ACC") {
  const positions = await getDriverRacePositions(driverId, sim, true);
  return positions.filter((pos) => pos === 0).length;
}

/**
 * Count the number of podiums (P1–P3 finishes).
 */
export async function getCurrentDriverPodiums(driverId, sim = "ACC") {
  const positions = await getDriverRacePositions(driverId, sim, true);
  return positions.filter((pos) => pos >= 0 && pos <= 2).length;
}
